/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getAddressesAction, getPartnerAddressesAction, resetStateAction, getListAddressFileAction } from './actions';
import {
	getAddressesSelector,
	getIsAddressesLoadingSelector,
	getPaginationSelector,
	getIsDownloadLoadingSelector,
} from './selectors';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import { ModalHeader } from './components/modal-header';
import { getIsAvailableAddressesButtonsSelector } from '../profile/selectors';
import { getIsAuditTabShownSelector } from '../partner-modal/selectors';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';
import { TABLE_COLUMNS } from './constants';

const AddressesContainerWrapper = ({
	onGetAddresses,
	addresses,
	onResetState,
	pagination,
	isChildren,
	onGetPartnerAddresses,
	isAvailable,
	isAddressesLoading,
	isAuditTabShown,
	onGetAddressFile,
	isDownloadLoading,
}) => {
	useInjectSaga({ key: 'addressesSaga', saga });
	useInjectReducer({ key: 'addressesReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const modal = query.get('modal');

	const id = query.get('id');

	const history = useHistory();

	const getAddresses = isChildren ? onGetPartnerAddresses : onGetAddresses;

	useEffect(() => {
		if (isChildren) {
			onResetState();
		}
		getAddresses({ searchQuery: query.toString(), id });
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'address');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		getAddresses({ searchQuery: query.toString(), id });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
			history.push(`${pathname}?${query}`);
		} else {
			query.set('per_page', value);
			history.push(`${pathname}?${query}`);
		}
		getAddresses({ searchQuery: query.toString(), id });
	};

	const handleNavItemClick = (modal, tab) => {
		query.set('modal', modal);

		if (tab) {
			query.set('tab', tab);
		}

		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		query.delete('modal');
		query.delete('mode');
		query.delete('id');
		query.delete('contactsSearch');
		query.delete('modalSorts');
		query.delete('modalFilters');

		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const handleDownloadAddressesButtonClick = () => {
		onGetAddressFile({ exportUrl: `/api/partners/${id}/addresses/export` });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		if (isChildren) {
			query.set('modalSorts', jsonSorts);
		} else {
			query.set('sorts', jsonSorts);
		}

		history.push(`${pathname}?${query}`);
		getAddresses({ searchQuery: query.toString(), id });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			getAddresses({ searchQuery: query.toString(), id });
		}
	});

	if (isChildren) {
		return (
			<ModalWindow
				onClose={handleModalClose}
				isModalShown
				navItems={[
					{
						id: 'info',
						text: 'Информация',
						onClick: () => handleNavItemClick('partner'),
						isActive: modal === 'partner',
					},
					{
						id: 'addresses',
						text: 'Адреса',
						onClick: () => handleNavItemClick('addresses'),
						isActive: modal === 'addresses',
					},
					{
						id: 'audit',
						text: 'Изменения',
						onClick: () => handleNavItemClick('partner', 'audit'),
						isActive: modal === 'partner' && isAuditTabShown,
					},
				]}
				modalContent={
					<DataGridComponent
						headerText="Адреса"
						onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
						columns={TABLE_COLUMNS()}
						rows={addresses}
						isTableDataLoading={isAddressesLoading}
						pagination={
							isChildren
								? null
								: {
										...pagination,
										onPageChange: handlePageChange,
										onSelectChange: handleSelectChange,
								  }
						}
						isChildren={isChildren}
						isWithoutFooter={isChildren}
						handleSortChange={handleSortChange}
						handleFilterChange={handleFilterChange}
						filterKey="modalFilters"
						sortKey="modalSorts"
					/>
				}
				modalHeader={
					<ModalHeader
						isDownloadLoading={isDownloadLoading}
						onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
						onDownloadButtonClick={isAvailable.export ? handleDownloadAddressesButtonClick : null}
						onModalClose={handleModalClose}
					/>
				}
				footerButtons={
					<ModalFooterButtons
						rightButtons={[
							{
								onClick: handleModalClose,
								text: 'Закрыть',
								color: 'secondary',
							},
						]}
					/>
				}
				style={{ width: '900px' }}
			/>
		);
	}

	const handleDownloadButtonClick = () => {
		onGetAddressFile({ exportUrl: '/api/addresses/export' });
	};

	return (
		<DataGridComponent
			headerText="Адреса"
			onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
			onDownloadButtonClick={isAvailable.export ? handleDownloadButtonClick : null}
			isDownloadButtonLoading={isDownloadLoading}
			columns={TABLE_COLUMNS()}
			rows={addresses}
			isTableDataLoading={isAddressesLoading}
			pagination={
				!pagination
					? null
					: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
			}
			handleSortChange={handleSortChange}
			handleFilterChange={handleFilterChange}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	addresses: getAddressesSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableAddressesButtonsSelector(),
	isAddressesLoading: getIsAddressesLoadingSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isDownloadLoading: getIsDownloadLoadingSelector(),
});

const mapDispatchToProps = {
	onGetAddressFile: getListAddressFileAction,
	onGetAddresses: getAddressesAction,
	onResetState: resetStateAction,
	onGetPartnerAddresses: getPartnerAddressesAction,
};

AddressesContainerWrapper.propTypes = {
	onGetAddressFile: PropTypes.func.isRequired,
	onGetAddresses: PropTypes.func.isRequired,
	addresses: PropTypes.array.isRequired,
	onResetState: PropTypes.func.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	isChildren: PropTypes.bool,
	onGetPartnerAddresses: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isAddressesLoading: PropTypes.bool.isRequired,
	isDownloadLoading: PropTypes.bool.isRequired,
	isAuditTabShown: PropTypes.bool.isRequired,
};

const AddressesContainer = connect(mapStateToProps, mapDispatchToProps)(AddressesContainerWrapper);

export default AddressesContainer;
